import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Dialog,
    DialogContent,
    Button,
    InputBase,
    IconButton,
    Grid,
    MenuItem,
    Select,
    Snackbar,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import mixpanel from 'mixpanel-browser';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import Model3DViewerPopup from '../handout/model3DViewerPopup';
import DownloadUtils from '../../../utils/download-utils';
import UserApi from '../../../rest-api/user-api';
import Model3DIcon from '../../../assets/icons/3D-Preview.jpg';
import canineFilterIcon from '../../../assets/icons/CanineFilter.svg';
import felineFilterIcon from '../../../assets/icons/FelineFilter.svg';
import addToBundleIcon from '../../../assets/icons/AddToBundle.svg';
import shareIcon from '../../../assets/icons/ShareEnvelop.svg';
import shareHandoutFlightIcon from '../../../assets/icons/ShareHandoutFlight.svg';
import CTAUpgradeDialog from '../../UIControls/CTAUpgradeDialog';
import { getCTAUpgradeImageUrl } from '../../../config';
import QuickShareDialog from '../handout/quick-share-dialog';
import { getHandoutBundleUpgradeTarget } from '../../../utils/subscription-plan-utils';

const locale = 'en-UK';
const normalize = (value) => (value || '').toString().trim().toLowerCase();

const deepContains = (value, target) => {
    const targetNormalized = normalize(target);
    if (!targetNormalized) return false;

    if (Array.isArray(value)) return value.some((entry) => deepContains(entry, targetNormalized));
    if (value && typeof value === 'object') {
        return Object.entries(value).some(
            ([key, nestedValue]) => normalize(key) === targetNormalized || deepContains(nestedValue, targetNormalized),
        );
    }
    if (typeof value === 'string') return normalize(value) === targetNormalized;
    return false;
};

const FILTERS = [
    { key: 'Canine', label: 'Canine', icon: canineFilterIcon },
    { key: 'Feline', label: 'Feline', icon: felineFilterIcon },
];

const getVisiblePages = (currentPage, pageCount) => {
    if (pageCount <= 5) {
        return Array.from({ length: pageCount }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
        return [1, 2, 3, 'ellipsis', pageCount];
    }

    if (currentPage >= pageCount - 2) {
        return [1, 'ellipsis', pageCount - 2, pageCount - 1, pageCount];
    }

    return [1, 'ellipsis', currentPage, 'ellipsis', pageCount];
};

const Models3DScreen = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
    const isPortrait = useMediaQuery('(orientation: portrait)');
    const isCompactLandscape = useMediaQuery('(orientation: landscape) and (max-height: 500px)');
    const usePhoneViewerMode = isPhone || isCompactLandscape;
    const isMobileLandscape = usePhoneViewerMode && !isPortrait;
    const { contentStore, imageStore, authStore, userDataStore } = useContext(StoreContext);

    const [models, setModels] = useState([]);
    const [imageMap, setImageMap] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilters, setActiveFilters] = useState([]);
    const [openModel, setOpenModel] = useState(null);

    const [itemsPerPage, setItemsPerPage] = useState(6);
    const [currentPage, setCurrentPage] = useState(1);

    const [upgradeCTAClinicOpen, setUpgradeCTAClinicOpen] = useState(false);
    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [quickShareOpen, setQuickShareOpen] = useState(false);
    const [quickShareHandout, setQuickShareHandout] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarStatus, setSnackbarStatus] = useState('success');

    useEffect(() => {
        const fetch3DModels = async () => {
            const contentArray = Object.values(contentStore.contentData);
            const modelsList = [];

            for (const item of contentArray) {
                const localeData = item.contentData[locale] || [];
                localeData.forEach((section) => {
                    section.blocks.forEach((block) => {
                        if (block.type === '3dModel') {
                            modelsList.push({
                                modelBlock: block,
                                handout: item,
                                contentName: block.title || item.contentName || '',
                            });
                        }
                    });
                });
            }

            setModels(modelsList);

            for (const model of modelsList) {
                const imageSrc = model.modelBlock?.imageSrc;
                if (!imageSrc) {
                    setImageMap((prev) => ({ ...prev, [imageSrc]: Model3DIcon }));
                    continue;
                }

                const imageObject = await imageStore.getImageFromIndexedDB(imageSrc);
                if (imageObject) {
                    setImageMap((prev) => ({ ...prev, [imageSrc]: imageObject.data }));
                    continue;
                }

                try {
                    const payload = {
                        files: [{ method: 'get', key: imageSrc }],
                        contentCategory: 'image',
                    };
                    const presignedUrl = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                    const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(
                        presignedUrl.getUrls[0]?.presignedUrl,
                    );

                    if (downloadResult?.base64data) {
                        await imageStore.saveImageToIndexedDB(imageSrc, downloadResult.base64data, downloadResult.etag);
                        setImageMap((prev) => ({ ...prev, [imageSrc]: downloadResult.base64data }));
                    } else {
                        setImageMap((prev) => ({ ...prev, [imageSrc]: Model3DIcon }));
                    }
                } catch (error) {
                    console.error(`Error fetching image: ${error}`);
                    setImageMap((prev) => ({ ...prev, [imageSrc]: Model3DIcon }));
                }
            }
        };

        if (contentStore.isInitialized) fetch3DModels();
    }, [contentStore, imageStore, authStore]);

    const filteredModels = useMemo(() => {
        let updatedModels = models;

        if (activeFilters.length) {
            updatedModels = updatedModels.filter((model) =>
                activeFilters.some((filter) => deepContains(model.handout.categories || {}, filter)),
            );
        }

        if (searchTerm.trim()) {
            const normalizedSearch = searchTerm.toLowerCase();
            updatedModels = updatedModels.filter((model) =>
                (model.contentName || '').toLowerCase().includes(normalizedSearch),
            );
        }

        return updatedModels;
    }, [models, activeFilters, searchTerm]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, activeFilters, itemsPerPage]);

    useEffect(() => {
        setItemsPerPage(isMobile ? 4 : 6);
    }, [isMobile]);

    const handleFilterToggle = (filterKey) => {
        setActiveFilters((current) =>
            current.includes(filterKey) ? current.filter((entry) => entry !== filterKey) : [...current, filterKey],
        );
    };

    const pageCount = Math.max(1, Math.ceil(filteredModels.length / itemsPerPage));
    const safePage = Math.min(currentPage, pageCount);
    const paginatedModels = filteredModels.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);
    const visiblePages = getVisiblePages(safePage, pageCount);

    const openModelPopup = (model) => {
        setOpenModel(model);
        mixpanel.track('3DModelOpened', {
            email: userDataStore?.userData?.emailAddress,
            modelTitle: model?.modelBlock?.title || model?.handout?.contentName,
            handoutId: model?.handout?.contentId,
        });
    };

    const handleAddToBundle = async (event, handout) => {
        event.stopPropagation();
        const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';

        const upgradeTarget = getHandoutBundleUpgradeTarget(userDataStore?.subscriptionData?.accountType);
        if (upgradeTarget === 'professional') {
            setUpgradeCTAOpen(true);
            return;
        }
        if (upgradeTarget === 'clinic') {
            setUpgradeCTAClinicOpen(true);
            return;
        }

        const userHandoutsJson = userDataStore.userData.handouts || '[]';
        const userHandouts = Array.isArray(userDataStore.userData.handouts)
            ? userDataStore.userData.handouts
            : JSON.parse(userHandoutsJson);

        const existingContentIds = userHandouts.map((entry) => entry.contentId);
        if (existingContentIds.includes(handout.contentId)) {
            setSnackbarMessage('Handout already exists in the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }

        if (userHandouts.length >= 5) {
            setSnackbarMessage('Cannot add more than 5 handouts to the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }

        userHandouts.push({ contentId: handout.contentId, contentName: handout.contentName, categories: '' });
        const payload = { handouts: userHandouts };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
            userDataStore.updateUserHandouts(userHandouts);
            setSnackbarMessage('Handout added to bundle');
            setSnackbarStatus('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error adding handout to bundle:', error);
            setSnackbarMessage('Failed to add handout to bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
        }
    };

    const handleQuickShare = (event, handout) => {
        event.stopPropagation();
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            setUpgradeCTAOpen(true);
            return;
        }
        setQuickShareHandout(handout);
        setQuickShareOpen(true);
    };

    const handleQuickShareFromViewer = (handout) => {
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            setUpgradeCTAOpen(true);
            return;
        }
        setQuickShareHandout(handout);
        setQuickShareOpen(true);
    };

    return (
        <Box
            sx={{
                px: { xs: '16px', md: '20px' },
                pb: { xs: '24px', md: '24px' },
                background: '#F0F0FC',
                minHeight: 'calc(100vh - 140px)',
            }}
        >
            <Box sx={{ maxWidth: '1360px', margin: '0 auto', pt: { xs: '20px', md: '34px' } }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '16px',
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                    }}
                >
                    <Typography
                        sx={{
                            color: '#000453',
                            fontSize: { xs: '24px', md: '40px' },
                            lineHeight: { xs: '24px', md: '40px' },
                            fontWeight: 600,
                            letterSpacing: { xs: '-0.96px', md: '-1.6px' },
                        }}
                    >
                        3D models
                    </Typography>
                    <Box
                        sx={{
                            borderRadius: '89.324px',
                            background: 'linear-gradient(176.249deg, #FF669B 0%, #BD8DE8 110.58%)',
                            height: '50px',
                            width: { xs: '100%', md: '251px' },
                            display: 'flex',
                            alignItems: 'center',
                            px: '16px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <SearchIcon sx={{ color: '#FFFFFF', opacity: 0.8, fontSize: 20, mr: 1, flexShrink: 0 }} />
                        <InputBase
                            type="text"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="Search models..."
                            fullWidth
                            inputProps={{ spellCheck: false }}
                            sx={{
                                width: '100%',
                                height: '100%',
                                background: 'transparent',
                                color: 'white',
                                '& .MuiInputBase-input': {
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    appearance: 'none',
                                    padding: 0,
                                    border: 0,
                                    borderRadius: 0,
                                    outline: 0,
                                    margin: 0,
                                    backgroundColor: 'transparent !important',
                                    boxShadow: 'none',
                                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                                    color: 'white',
                                    WebkitTextFillColor: 'white',
                                    fontSize: '16.078px',
                                    lineHeight: '16.078px',
                                    letterSpacing: '-0.3216px',
                                    fontWeight: 400,
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(255,255,255,0.95)',
                                    opacity: 1,
                                },
                                '& input:-webkit-autofill': {
                                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                                    WebkitTextFillColor: 'white !important',
                                    transition: 'background-color 9999s ease-out 0s',
                                },
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        mt: '24px',
                        background: '#FFFFFF',
                        borderRadius: '30px',
                        p: { xs: '14px', md: '20px' },
                        border: '1px solid #ECECFA',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: '10px', mb: '18px' }}>
                        {FILTERS.map((filter) => {
                            const isActive = activeFilters.includes(filter.key);
                            return (
                                <Box
                                    key={filter.key}
                                    onClick={() => handleFilterToggle(filter.key)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderRadius: '224px',
                                        px: '18px',
                                        height: '44px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        border: isActive ? '1px solid #000453' : '1px solid #E5E5E5',
                                        background: isActive ? '#000453' : '#FFFFFF',
                                        color: isActive ? '#FFFFFF' : '#616063',
                                    }}
                                >
                                    <img
                                        src={filter.icon}
                                        alt=""
                                        width={24}
                                        height={24}
                                        style={{
                                            opacity: isActive ? 1 : 0.9,
                                            transform: 'scale(1.08)',
                                            transformOrigin: 'center',
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '18px',
                                            lineHeight: '18px',
                                            fontWeight: 500,
                                            letterSpacing: '-0.36px',
                                            opacity: isActive ? 1 : 0.5,
                                        }}
                                    >
                                        {filter.label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {paginatedModels.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: '48px' }}>
                            <Typography sx={{ color: '#616063', fontSize: '18px' }}>No models available.</Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {paginatedModels.map((model) => {
                                const modelBlock = model.modelBlock;
                                const handout = model.handout;
                                const imageSrc = modelBlock?.imageSrc;

                                return (
                                    <Grid item xs={12} md={6} key={`${handout.contentId}-${modelBlock.uid}`}>
                                        <Box
                                            sx={{
                                                background: '#F0F0FC',
                                                borderRadius: '20px',
                                                p: { xs: '13.91px', md: '10px' },
                                                minHeight: { xs: '411.363px' },
                                                height: { md: '440px' },
                                                width: { md: '649px' },
                                                maxWidth: '100%',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    borderRadius: '10px',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    background: '#000453',
                                                    width: '100%',
                                                    margin: '0 auto',
                                                    aspectRatio: { xs: '284.178 / 254.369', md: '618 / 348' },
                                                    maxHeight: { md: '348px' },
                                                }}
                                                onClick={() => openModelPopup(model)}
                                            >
                                                <img
                                                    src={imageMap[imageSrc] || Model3DIcon}
                                                    alt={modelBlock?.alt}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                    }}
                                                />
                                                <PlayCircleFilledWhiteIcon
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        fontSize: isMobile ? '58px' : '64px',
                                                        color: 'rgba(255,255,255,0.92)',
                                                    }}
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    pt: '10px',
                                                    px: { xs: 0, md: '2px' },
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: { xs: 'none', md: 'flex' },
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '10px',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color: '#000453',
                                                            fontFamily: 'Inter, sans-serif',
                                                            fontSize: '26px',
                                                            lineHeight: '26px',
                                                            fontWeight: 600,
                                                            letterSpacing: '-0.52px',
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            pr: '8px',
                                                        }}
                                                    >
                                                        {modelBlock.title || handout.contentName}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '10px',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <Box
                                                            onClick={(event) => handleAddToBundle(event, handout)}
                                                            sx={{
                                                                width: 50,
                                                                height: 50,
                                                                borderRadius: '50%',
                                                                background: '#FF669B',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <img src={addToBundleIcon} alt="" width={22} height={22} />
                                                        </Box>
                                                        <Box
                                                            onClick={(event) => handleQuickShare(event, handout)}
                                                            sx={{
                                                                width: 50,
                                                                height: 50,
                                                                borderRadius: '50%',
                                                                background: '#FF669B',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <img src={shareIcon} alt="" width={22} height={22} />
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                                    <Typography
                                                        sx={{
                                                            color: '#000453',
                                                            fontFamily: 'Inter, sans-serif',
                                                            fontSize: '20px',
                                                            lineHeight: '24px',
                                                            fontWeight: 600,
                                                            letterSpacing: '-0.4px',
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            whiteSpace: 'normal',
                                                            minHeight: '48px',
                                                        }}
                                                    >
                                                        {modelBlock.title || handout.contentName}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            borderTop: '2px solid #D8D8EA',
                                                            mt: '12px',
                                                            pt: '10px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-end',
                                                            gap: '10px',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <Box
                                                            onClick={(event) => handleAddToBundle(event, handout)}
                                                            sx={{
                                                                width: '49.682px',
                                                                height: '49.682px',
                                                                borderRadius: '50%',
                                                                background: '#FF669B',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <img src={addToBundleIcon} alt="" width={22} height={22} />
                                                        </Box>
                                                        <Box
                                                            onClick={(event) => handleQuickShare(event, handout)}
                                                            sx={{
                                                                width: '49.682px',
                                                                height: '49.682px',
                                                                borderRadius: '50%',
                                                                background: '#FF669B',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <img src={shareIcon} alt="" width={22} height={22} />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}

                    <Box
                        sx={{
                            mt: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '8px',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <IconButton
                                size="small"
                                disabled={safePage <= 1}
                                onClick={() => setCurrentPage((value) => Math.max(1, value - 1))}
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: '42px',
                                    border: '1.313px solid #E5E5E5',
                                    background: '#FFFFFF',
                                    color: '#6D6D6D',
                                    '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                }}
                            >
                                <ChevronLeftIcon sx={{ fontSize: '15.8px' }} />
                            </IconButton>
                            {visiblePages.map((pageItem, index) => {
                                if (pageItem === 'ellipsis') {
                                    return (
                                        <Box
                                            key={`ellipsis-${index}`}
                                            sx={{
                                                width: 42,
                                                height: 42,
                                                borderRadius: '8px',
                                                background: '#FFFFFF',
                                                color: '#616063',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '21px',
                                                lineHeight: '21px',
                                            }}
                                        >
                                            ...
                                        </Box>
                                    );
                                }

                                const isActive = pageItem === safePage;
                                return (
                                    <Box
                                        key={pageItem}
                                        onClick={() => setCurrentPage(pageItem)}
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: '42px',
                                            border: isActive ? 'none' : '1.313px solid #E5E5E5',
                                            background: isActive ? '#FF669B' : '#FFFFFF',
                                            color: isActive ? '#FFFFFF' : '#616063',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '16px',
                                            lineHeight: '16px',
                                            fontWeight: isActive ? 400 : 500,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {pageItem}
                                    </Box>
                                );
                            })}
                            <IconButton
                                size="small"
                                disabled={safePage >= pageCount}
                                onClick={() => setCurrentPage((value) => Math.min(pageCount, value + 1))}
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: '42px',
                                    border: '1.313px solid #E5E5E5',
                                    background: '#FFFFFF',
                                    color: '#6D6D6D',
                                    '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                }}
                            >
                                <ChevronRightIcon sx={{ fontSize: '15.8px' }} />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography
                                sx={{
                                    color: '#616063',
                                    fontSize: { xs: '16px', md: '13px' },
                                    lineHeight: { xs: '16px', md: '13px' },
                                }}
                            >
                                Items per page
                            </Typography>
                            <Select
                                size="small"
                                value={itemsPerPage}
                                onChange={(event) => setItemsPerPage(Number(event.target.value))}
                                sx={{
                                    width: { xs: 63, md: 'auto' },
                                    height: { xs: 30, md: 30 },
                                    borderRadius: { xs: '10px', md: '10px' },
                                    fontSize: { xs: '16px', md: '13px' },
                                    color: '#616063',
                                    '& .MuiSelect-select': {
                                        py: 0,
                                        minHeight: 'unset',
                                        lineHeight: { xs: '16px', md: '13px' },
                                        pl: { xs: '15px', md: '10px' },
                                        pr: { xs: '26px', md: '24px' },
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#E5E5E5',
                                        borderWidth: '1px',
                                    },
                                    '& .MuiSelect-icon': {
                                        right: { xs: '8px', md: '8px' },
                                        color: '#616063',
                                    },
                                }}
                            >
                                {[4, 6, 8, 10, 12].map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {openModel && (
                <Dialog
                    open
                    onClose={() => setOpenModel(null)}
                    maxWidth="lg"
                    fullWidth
                    fullScreen={usePhoneViewerMode}
                    sx={{
                        '& .MuiDialog-paper': {
                            borderRadius: usePhoneViewerMode ? 0 : '24px',
                            overflow: 'hidden',
                        },
                    }}
                >
                    <DialogContent
                        sx={{
                            p: 0,
                            m: 0,
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: usePhoneViewerMode ? 'center' : 'space-between',
                            minHeight: usePhoneViewerMode ? '100dvh' : 'auto',
                            pb: usePhoneViewerMode ? (isMobileLandscape ? '85px' : '98px') : 0,
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                flex: usePhoneViewerMode ? 1 : 'unset',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Model3DViewerPopup
                                uid={openModel.modelBlock.uid}
                                alt={openModel.modelBlock.alt}
                                contentId={openModel.handout.contentId}
                            />
                            {usePhoneViewerMode && (
                                <IconButton
                                    onClick={() => setOpenModel(null)}
                                    sx={{
                                        position: 'absolute',
                                        top: '14px',
                                        right: '14px',
                                        zIndex: 1500,
                                        width: '35px',
                                        height: '35px',
                                        background: '#ECECEC',
                                        color: '#5E5E5E',
                                        '&:hover': { background: '#E2E2E2' },
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                        {usePhoneViewerMode ? (
                            <Box
                                sx={{
                                    position: 'fixed',
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 1400,
                                    background: '#FFFFFF',
                                    borderTop: '1px solid #E5E5E5',
                                    height: isMobileLandscape ? '85px' : '98px',
                                    px: isMobileLandscape ? '24px' : '7.27px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: isMobileLandscape ? 'space-between' : 'flex-start',
                                }}
                            >
                                <IconButton
                                    onClick={(event) => handleAddToBundle(event, openModel.handout)}
                                    sx={{
                                        width: 47,
                                        height: 47,
                                        borderRadius: '50%',
                                        backgroundColor: '#FF669B',
                                        '&:hover': { backgroundColor: '#e65588' },
                                    }}
                                >
                                    <img src={addToBundleIcon} alt="" style={{ width: 22, height: 22 }} />
                                </IconButton>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginLeft: 'auto',
                                        gap: isMobileLandscape ? '24px' : '7.54px',
                                    }}
                                >
                                    <Button
                                        onClick={() => handleQuickShareFromViewer(openModel.handout)}
                                        sx={{
                                            height: 47,
                                            borderRadius: '188px',
                                            backgroundColor: '#FF669B',
                                            color: '#FFFFFF',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '9.4px',
                                            px: '18.8px',
                                            fontFamily: 'Inter',
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            lineHeight: '14px',
                                            letterSpacing: '-0.28px',
                                            textTransform: 'none',
                                            whiteSpace: 'nowrap',
                                            minWidth: isMobileLandscape ? 'auto' : 'unset',
                                            width: isMobileLandscape ? 'auto' : '166px',
                                            boxShadow: '0px 0px 9.4px rgba(35, 7, 88, 0.1)',
                                            '&:hover': { backgroundColor: '#e65588' },
                                        }}
                                    >
                                        <img src={shareHandoutFlightIcon} alt="" style={{ width: 17, height: 17 }} />
                                        Share 3D model
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                    height: '74px',
                                    padding: '0 20px',
                                    background: '#FFFFFF',
                                }}
                            >
                                <IconButton
                                    onClick={(event) => handleAddToBundle(event, openModel.handout)}
                                    sx={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: '50%',
                                        backgroundColor: '#FF669B',
                                        '&:hover': { backgroundColor: '#e65588' },
                                    }}
                                >
                                    <img src={addToBundleIcon} alt="" style={{ width: 22, height: 22 }} />
                                </IconButton>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <Button
                                        onClick={() => handleQuickShareFromViewer(openModel.handout)}
                                        sx={{
                                            width: '204.95px',
                                            height: '50px',
                                            borderRadius: '188px',
                                            backgroundColor: '#FF669B',
                                            color: '#FFFFFF',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '9.4px',
                                            px: '18.8px',
                                            fontFamily: 'Inter',
                                            fontWeight: 500,
                                            fontSize: '18px',
                                            lineHeight: '18px',
                                            letterSpacing: '-0.36px',
                                            textTransform: 'none',
                                            whiteSpace: 'nowrap',
                                            minWidth: 'auto',
                                            boxShadow: '0px 0px 9.4px rgba(35, 7, 88, 0.1)',
                                            '&:hover': { backgroundColor: '#e65588' },
                                        }}
                                    >
                                        <img src={shareHandoutFlightIcon} alt="" style={{ width: 22, height: 22 }} />
                                        Share 3D model
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                </Dialog>
            )}

            <CTAUpgradeDialog
                open={upgradeCTAClinicOpen}
                handleClose={() => setUpgradeCTAClinicOpen(false)}
                title="Get more from Cetra!"
                message="You've discovered a feature available on our paid plans. Upgrade to access all the tools you need to make Cetra part of your everyday workflow."
                buttonText="Upgrade plan"
                benefits={[
                    'Save, share and print unlimited handouts',
                    'Save and share customised & custom built handouts',
                    'Add up to 4 additional team members',
                    'Personalise with your clinic logo',
                    'Create bundles of handouts',
                    'Connect with supported PMS integrations',
                ]}
                footnote="… and more!"
                imageUrl={getCTAUpgradeImageUrl('clinic')}
            />

            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => setUpgradeCTAOpen(false)}
                title="Get more from Cetra!"
                message="You've discovered a feature available on our paid plans. Upgrade to access all the tools you need to make Cetra part of your everyday workflow."
                buttonText="Upgrade plan"
                benefits={[
                    'Save, share and print unlimited handouts',
                    'Save customised and custom built handouts',
                    'Personalise with your clinic details',
                    'Connect with supported PMS integrations',
                ]}
                footnote="… and more!"
                imageUrl={getCTAUpgradeImageUrl('professional')}
            />

            {quickShareHandout && (
                <QuickShareDialog
                    open={quickShareOpen}
                    handout={quickShareHandout}
                    onClose={() => {
                        setQuickShareOpen(false);
                        setQuickShareHandout(null);
                    }}
                />
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={
                    <Box display="flex" alignItems="center">
                        {snackbarMessage}
                        {snackbarStatus === 'success' ? (
                            <CheckIcon sx={{ ml: 1, color: 'white' }} />
                        ) : (
                            <CloseIcon sx={{ ml: 1, color: 'red' }} />
                        )}
                    </Box>
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundImage: 'linear-gradient(132deg, #FA96C3 -2.07%, #FF669B 15.82%, #BD8DE8 116.64%)',
                        display: 'flex',
                        justifyContent: 'center',
                        borderRadius: 2,
                    },
                }}
            />
        </Box>
    );
};

export default Models3DScreen;
